import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import {
  getSessionSnapshot,
  isSessionRejected,
  refreshUnlessRenewed,
} from 'services/session';
import redirectToAuth from 'utils/redirectToAuth';
import removeAuthCookies from 'utils/removeAuthCookies';

type RetriableRequestConfig = AxiosRequestConfig & {
  retried?: boolean;
  sessionSnapshot?: string;
};

export const chatAPI = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL_CHAT,
  withCredentials: true,
});

function handleUnauthorized() {
  removeAuthCookies();
  redirectToAuth();
}

async function onResponseError(error: AxiosError): Promise<unknown> {
  const config = error.config as RetriableRequestConfig | undefined;

  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  // Erros das rotas de auth (ex.: senha atual incorreta) não indicam sessão expirada
  if (config?.url?.startsWith('v2/auth/')) {
    return Promise.reject(error);
  }

  // 401 mesmo após renovar: a sessão é válida (o refresh passou), então é recusa deste
  // endpoint. Redirecionar criaria um laço, pois o auth devolve o usuário para cá
  if (!config || config.retried) {
    return Promise.reject(error);
  }

  config.retried = true;

  try {
    await refreshUnlessRenewed(config.sessionSnapshot);
  } catch (refreshError) {
    // Só o refresh recusado indica sessão inválida; core instável só falha esta requisição
    if (isSessionRejected(refreshError)) handleUnauthorized();

    return Promise.reject(error);
  }

  return chatAPI.request(config);
}

// Guarda com qual sessão a requisição saiu, para o 401 saber se outra aba já renovou
chatAPI.interceptors.request.use((config: RetriableRequestConfig) => {
  config.sessionSnapshot = getSessionSnapshot();

  return config;
});

chatAPI.interceptors.response.use(undefined, onResponseError);
